import {
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";

import {
  clampTreeWidth,
  readTreeCollapsed,
  readTreeWidth,
  writeTreeCollapsed,
  writeTreeWidth,
} from "@/modules/docs/treeLayout";

/**
 * The left tree pane's width and collapsed state (#403): draggable between
 * 200 and 480px, collapsible to a thin rail, both remembered per browser.
 *
 * Read once at mount rather than through a `useEffect` syncing from
 * `localStorage` — the width is only ever this browser's own preference, not
 * something another tab or a server response could change under it, so
 * `useState`'s lazy initialiser is the whole story.
 *
 * The three handle props are meant for one element, the same way
 * `ScreenshotCanvas` wires `onPointerDown`/`onPointerMove`/`onPointerUp` to
 * one `<canvas>`: `setPointerCapture` on down is what keeps the drag tracking
 * once the cursor leaves the handle's own thin hitbox.
 */
export interface ResizableTree {
  width: number;
  collapsed: boolean;
  toggleCollapsed: () => void;
  handleProps: {
    onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
    onPointerMove: (event: ReactPointerEvent<HTMLDivElement>) => void;
    onPointerUp: (event: ReactPointerEvent<HTMLDivElement>) => void;
  };
}

export function useResizableTree(): ResizableTree {
  const [width, setWidth] = useState<number>(() => readTreeWidth());
  const [collapsed, setCollapsed] = useState<boolean>(() =>
    readTreeCollapsed(),
  );
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev;
      writeTreeCollapsed(next);
      return next;
    });
  }

  function onPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { startX: event.clientX, startWidth: width };
  }

  function onPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag) return;
    setWidth(clampTreeWidth(drag.startWidth + (event.clientX - drag.startX)));
  }

  function onPointerUp(event: ReactPointerEvent<HTMLDivElement>) {
    if (!dragRef.current) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
    // Reads back through `setWidth`'s updater rather than closing over the
    // `width` state directly, which could be one drag-frame stale here.
    setWidth((current) => {
      writeTreeWidth(current);
      return current;
    });
  }

  return {
    width,
    collapsed,
    toggleCollapsed,
    handleProps: { onPointerDown, onPointerMove, onPointerUp },
  };
}
